import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  getSavingsPlan,
  resetGoalToLaunch,
  getDashboardInformation,
  resetSavingsPlan,
  resetDashboardData,
  setFirstLogin,
  setGoalToLaunch,
  setGoalToLaunchPayload,
} from "../redux/actions";
import styled from "styled-components";
import theme from "../theme/theme";
import Card from "../components/Base/Card";
import Header from "../components/Header";
import InvestmentHighlights from "../components/InvestmentHighlights";
import DashboardHeader from "../components/DashboardHeader";
import PortfolioChart from "../components/PortfolioChart/PortfolioChart";
import RecommendedPortfolio from "../components/RecommendedPortfolio/RecommendedPortfolio";
import StrategyPicker from "../components/StrategyPicker/StrategyPicker";
import GoalSettings from "../components/GoalSettings/GoalSettings";
import FinacialAdvisorDetailsModal from "../components/FinacialAdvisorDetailsModal/FinacialAdvisorDetailsModal";
import LoadingBar from "../components/LoadingBar/LoadingBar";
import { SET_LOADING } from "../redux/action-types";
import { analytics } from "../analytics";
import { createPayload } from "../utils";

const Wrapper = styled.div`
  display: flex;
  padding: 30px 30px;
  gap: 30px;

  .mainPanel {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;
    flex: 1;

    color: ${theme.colors.primaryStrong};

    @media (max-width: 480px) {
      margin-top: 6%;
    }
  }

  .rightPanel {
    min-width: 200px;
    width: 310px;

    @media (max-width: 480px) {
      padding: 0% 0%;
      margin-top: 5%;
    }
  }

  .break {
    margin: 100px 0 50px 0;
    border-bottom: 1px solid ${theme.colors.gray};
    @media (max-width: 480px) {
      padding: 0% 0%;
      margin-top: 5%;
    }
  }
`;

const WrapperForMobile = styled.div`
  max-width: 100vw;
  padding: 2% 5%;

  .mainPanel {
    @media (max-width: 480px) {
      margin-top: 5%;
    }
  }

  .leftPanel {
    @media (max-width: 480px) {
      padding: 0% 0%;
      margin-top: 5%;
    }
  }

  .rightPanel {
    @media (max-width: 480px) {
      padding: 0% 0%;
      margin-top: 5%;
    }
  }
`;

const StyledLink = styled.a`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${theme.colors.primaryStrong};
  border-color: ${theme.colors.primaryStrong};
  color: ${theme.colors.white};
  padding: 15px;
  font-weight: bold;
  font: 16px;
  letter-spacing: 0px;
  margin-top: 12px;
  text-decoration: none;
  border-radius: 4px;
  box-sizing: border-box;

  &.large {
    padding: 58px;
    font-size: 1.5em;
  }

  &.disabled {
    pointer-events: none;
    background-color: ${theme.colors.gray1};
    border-color: ${theme.colors.gray1};
  }
`;

const goalNames = ["Retirement", "Major Purchase", "Education", "Build Wealth"];

const Dashboard = () => {
  const history = useHistory();
  const {
    loading,
    dashboardData,
    goal,
    setGoalToLaunchDetails,
    savingsPlanSuccess,
    savingsPlanId,
    userAccountDetails,
  } = useSelector((state) => state.user);
  const [isLoaded, setIsLoaded] = useState(true);
  const dispatch = useDispatch();
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [isFinacialAdvisorModalVisible, setIsFinacialAdvisorModalVisible] =
    useState(false);
  const [selectedFinanCialAdvisorOption, setSelectedFinancialAdvisorOption] =
    useState();
  const [savingPlansAcc, setSavingPlansAcc] = useState(0);

  useEffect(() => {
    if (loading) {
      setIsLoaded(false);
    }
  }, [loading]);

  useEffect(() => {
    dispatch({ type: SET_LOADING, payload: true });
    dispatch(resetDashboardData());
  }, [dispatch]);

  useEffect(() => {
    if (!dashboardData) {
      dispatch({ type: SET_LOADING, payload: true });
    } else if (savingPlansAcc === 0) {
      dispatch({ type: SET_LOADING, payload: true });
      setSavingPlansAcc((prevAcc) => prevAcc + 1);
    } else if (
      dashboardData &&
      goal &&
      savingPlansAcc === 1 &&
      savingsPlanId &&
      userAccountDetails
    ) {
      dispatch({ type: SET_LOADING, payload: false });
    }
  }, [
    dashboardData,
    dispatch,
    goal,
    savingPlansAcc,
    savingsPlanId,
    userAccountDetails,
  ]);

  useEffect(() => {
    dispatch(
      getSavingsPlan({
        goalId: goal.id,
      })
    );
  }, [dispatch, goal.id]);

  useEffect(() => {
    if (
      dashboardData &&
      dashboardData.isRequestValid !== undefined &&
      dashboardData.isRequestValid === false
    ) {
      console.log("inside error ");
    }
  }, [dashboardData]);

  useEffect(() => {
    if (setGoalToLaunchDetails) {
      dispatch(resetGoalToLaunch());
      if (
        selectedFinanCialAdvisorOption === 1 ||
        selectedFinanCialAdvisorOption === 3
      )
        history.push("/select-custodian-type");
      else history.push("/select-custodian-type");
    }
  }, [
    setGoalToLaunchDetails,
    history,
    dispatch,
    selectedFinanCialAdvisorOption,
  ]);

  useEffect(() => {
    if (savingsPlanSuccess) {
      dispatch(getDashboardInformation(goal.id));
      dispatch(resetSavingsPlan());
    }
  }, [savingsPlanSuccess, dispatch, goal.id]);

  const displayText = () => {
    if (dashboardData?.yourGoal?.goal === 1) {
      return (
        <p>
          *Please confirm your maximum contribution amount with your tax advisor
        </p>
      );
    }
  };

  const getGoalData = (data) => {
    let goalTypeIndex, goalMonthly, goalTime, goalInitial, goalAmount;
    data.forEach((element) => {
      if (element.subject.replace(/[^\x00-\x7F]/g, "") === "Risk Profile") {
        goalTypeIndex = element.value;
      }
      if (element.subject === "Monthly Contribution") {
        goalMonthly = element.value;
      }
      if (
        element.subject === "Years Shown" ||
        element.subject === "Years Remaining" ||
        element.subject === "Retirement Age"
      ) {
        goalTime = element.value;
      }
      if (element.subject === "Initial Deposit") {
        goalInitial = element.value;
      }
      if (
        element.subject === "Goal Amount" ||
        element.subject === "Retirement Income Goal"
      ) {
        goalAmount = element.value;
      }
    });

    const goalRisks = [
      "Very Conservative",
      "Conservative",
      "Balanced",
      "Adventurous",
      "Very Adventurous",
    ];
    return {
      goalRisk: goalRisks[goalTypeIndex - 1],
      goalMonthly: goalMonthly,
      goalTime: goalTime,
      goalInitial: goalInitial || "NA",
      goalAmount: goalAmount || "NA",
    };
  };

  const launchRequestAnalytics = () => {
    const today = new Date();
    analytics.track("Launch Requested", {
      userId: userAccountDetails?.id || "",
      goalId: goal?.id || "",
      goalType: goalNames[dashboardData?.yourGoal?.goal - 1],
      questionnaireUpdated: today.toLocaleString(),
      goalRisk:
        getGoalData(dashboardData?.settings?.additionalInfos).goalRisk || "",
      goalMonthly:
        getGoalData(dashboardData?.settings?.additionalInfos).goalMonthly || "",
      goalTime:
        getGoalData(dashboardData?.settings?.additionalInfos).goalTime || "",
      goalInitial:
        getGoalData(dashboardData?.settings?.additionalInfos).goalInitial || "",
      goalAmount:
        getGoalData(dashboardData?.settings?.additionalInfos).goalAmount || "",
    });
  };

  const handleReadyToLaunchClick = () => {
    dispatch(setFirstLogin(false));
    launchRequestAnalytics();
    if (userAccountDetails?.advisorPreference == null) {
      setIsFinacialAdvisorModalVisible(true);
    } else {
      dispatch(
        setGoalToLaunch(
          createPayload(
            dashboardData,
            userAccountDetails?.advisorPreference,
            userAccountDetails?.advisor,
            goal
          )
        )
      );
      dispatch(
        setGoalToLaunchPayload(
          createPayload(
            dashboardData,
            userAccountDetails?.advisorPreference,
            userAccountDetails?.advisor,
            goal
          )
        )
      );
    }
  };

  return (
    <>
      <Header />
      {(!isLoaded || loading) && (
        <LoadingBar isLoading={loading} setIsLoaded={setIsLoaded} />
      )}
      {isFinacialAdvisorModalVisible && (
        <FinacialAdvisorDetailsModal
          onClose={setIsFinacialAdvisorModalVisible}
          selectedOption={setSelectedFinancialAdvisorOption}
        />
      )}
      {window.screen.width > 480 ? (
        <>
          {(!loading || dashboardData || isLoaded) && (
            <Wrapper>
              <Card className="leftPanel">
                <StrategyPicker data={dashboardData?.strategies} />
              </Card>
              <Card className="mainPanel">
                <DashboardHeader
                  btnDisabled={btnDisabled}
                  selectedOption={setSelectedFinancialAdvisorOption}
                />
                <InvestmentHighlights
                  data={dashboardData?.projectedPortfolio}
                />
                <PortfolioChart
                  data={dashboardData?.dashboardGraphInformation?.graphValues}
                />
                <div className="break"></div>
                <RecommendedPortfolio
                  portfolios={dashboardData?.recommendedPortfolios?.portfolios}
                  topHoldings={dashboardData?.topHoldings}
                />
                <StyledLink
                  // href="https://gdcdyn.interactivebrokers.com/Universal/Application"
                  // target="_blank"
                  onClick={handleReadyToLaunchClick}
                  className={`readyToLaunc ${
                    btnDisabled ? "disabled" : ""
                  } large`}
                >
                  READY TO LAUNCH
                </StyledLink>
              </Card>
              <div className="rightPanel">
                <GoalSettings
                  data={dashboardData}
                  setBtnDisabled={setBtnDisabled}
                />
                <StyledLink
                  // href="https://gdcdyn.interactivebrokers.com/Universal/Application"
                  // target="_blank"
                  onClick={handleReadyToLaunchClick}
                  className={`${btnDisabled ? "disabled" : ""}`}
                >
                  READY TO LAUNCH
                </StyledLink>
                {displayText()}
              </div>
            </Wrapper>
          )}
        </>
      ) : (
        <>
          {(!loading || dashboardData) && (
            <WrapperForMobile>
              <StyledLink
                // href="https://gdcdyn.interactivebrokers.com/Universal/Application"
                // target="_blank"
                onClick={handleReadyToLaunchClick}
                className={`${btnDisabled ? "disabled" : ""}`}
              >
                READY TO LAUNCH
              </StyledLink>
              <Card className="leftPanel">
                <StrategyPicker data={dashboardData?.strategies} />
              </Card>
              <div className="rightPanel">
                <GoalSettings
                  data={dashboardData}
                  setBtnDisabled={setBtnDisabled}
                />
              </div>
              <Card className="mainPanel">
                <DashboardHeader btnDisabled={btnDisabled} />
                <PortfolioChart
                  data={dashboardData?.dashboardGraphInformation?.graphValues}
                  graphStartingPoint={
                    dashboardData?.dashboardGraphInformation?.graphStartingPoint
                  }
                />
                <div style={{ marginTop: "25%" }}>
                  <InvestmentHighlights
                    data={dashboardData?.projectedPortfolio}
                  />
                </div>
                <RecommendedPortfolio
                  portfolios={dashboardData?.recommendedPortfolios?.portfolios}
                />
              </Card>
              <StyledLink
                // href="https://gdcdyn.interactivebrokers.com/Universal/Application"
                // target="_blank"
                onClick={() => {
                  launchRequestAnalytics();
                  dispatch(setFirstLogin(false));
                  setIsFinacialAdvisorModalVisible(true);
                }}
                className={`${btnDisabled ? "disabled" : ""}`}
              >
                READY TO LAUNCH
              </StyledLink>
              {displayText()}
            </WrapperForMobile>
          )}
        </>
      )}
    </>
  );
};

export default Dashboard;
